import { hint } from "@i18n";
import { createTheme, type MantineColorsTuple } from "@mantine/core";
import { decimalSeparator } from "@ui/format";

// the breeding green of the design (#2f6b3a at index 6) and the wheat gold accent
const breeder: MantineColorsTuple = [
  "#eef6ef",
  "#dcebde",
  "#b6d6bb",
  "#8dc096",
  "#6aad77",
  "#539f62",
  "#2f6b3a",
  "#28603a",
  "#1f4a28",
  "#153a1d",
];
const gold: MantineColorsTuple = [
  "#fbf6e9",
  "#f6ebd6",
  "#ecd6a8",
  "#e2c077",
  "#d9ad4f",
  "#d4a237",
  "#c9a24a",
  "#a8832f",
  "#7a4f0e",
  "#5c3b09",
];

/** The Beállítások → Betűméret setting as a factor (matches the html[data-font-size] root sizes in design.css). */
export const FONT_SCALE = { small: 14.5 / 16, normal: 1, large: 18 / 16 } as const;

/** The theme in the current UI language (input hints, decimal separator); main.tsx rebuilds it when the language
 *  changes. */
export const buildTheme = () =>
  createTheme({
    primaryColor: "breeder",
    primaryShade: { light: 6, dark: 5 },
    colors: { breeder, gold },
    defaultRadius: "md",
    radius: { xs: "4px", sm: "6px", md: "8px", lg: "10px", xl: "14px" },
    fontFamily: "'Instrument Sans Variable', system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontFamilyMonospace: "'JetBrains Mono Variable', ui-monospace, monospace",
    // rem, so the Beállítások → Betűméret setting (the root font size) scales every text
    fontSizes: { xs: "0.6875rem", sm: "0.8125rem", md: "0.875rem", lg: "1rem", xl: "1.25rem" },
    headings: { fontWeight: "600" },
    spacing: { xs: "8px", sm: "12px", md: "16px", lg: "24px", xl: "32px" },
    respectReducedMotion: true,
    components: {
      Button: { defaultProps: { size: "sm", fw: 500 } },
      Textarea: { defaultProps: { size: "sm" } },
      TextInput: { defaultProps: { size: "sm" } },
      // no thousand separator: most whole numbers here are years, plot numbers and keys (2027, 900001), not amounts
      NumberInput: { defaultProps: { size: "sm", decimalSeparator: decimalSeparator() } },
      Select: {
        defaultProps: {
          size: "sm",
          placeholder: hint("select"),
          nothingFoundMessage: hint("noResults"),
          comboboxProps: { withinPortal: true },
        },
      },
      MultiSelect: { defaultProps: { size: "sm", placeholder: hint("select"), nothingFoundMessage: hint("noResults") } },
      Autocomplete: { defaultProps: { size: "sm", placeholder: hint("autocomplete") } },
      TagsInput: { defaultProps: { size: "sm", placeholder: hint("tags") } },
      SegmentedControl: {
        defaultProps: { transitionDuration: 220, transitionTimingFunction: "ease-out", classNames: { root: "choice-switch" } },
      },
      Card: { defaultProps: { withBorder: true, radius: "lg" } },
      Collapse: { defaultProps: { transitionDuration: 180, transitionTimingFunction: "ease-out" } },
      Popover: { defaultProps: { transitionProps: { transition: "fade", duration: 150 } } },
      Menu: { defaultProps: { transitionProps: { transition: "fade", duration: 150 } } },
      Modal: { defaultProps: { radius: "lg" } },
      Tooltip: { defaultProps: { openDelay: 300, withArrow: true } },
    },
  });
